import React, { FC, useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { Input, InputTypes, InputVariants, InputStates } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Form } from '@components/atoms/Form';
import { Space } from '@components/atoms/Space';
import styles from './ContactForm.module.css';
import { Typography } from '@components/atoms/Typography';

export interface ContactFormProps {
    onSubmit: (data: ContactFormData) => void;
    loading?: boolean;
    onSuccess?: () => void;
}

export interface ContactFormData {
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
}

export const ContactForm: FC<ContactFormProps> = ({ onSubmit, loading = false, onSuccess }) => {
    const { t } = useTranslation();
    const formRef = useRef<HTMLFormElement>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string[] }>({});

    const resetForm = () => {
        setName('');
        setEmail('');
        setPhone('');
        setSubject('');
        setMessage('');
        setRecaptchaToken(null);
        setErrors({});
        recaptchaRef.current?.reset();
    };

    React.useEffect(() => {
        if (onSuccess && !loading) {
            resetForm();
        }
    }, [onSuccess, loading]);

    const translations = {
        nameLabel: t('contact-form-component.name.label'),
        title: t('contact-form-component.title'),
        nameRequired: t('contact-form-component.name.required-error'),
        emailLabel: t('contact-form-component.email.label'),
        emailRequired: t('contact-form-component.email.required-error'),
        emailInvalid: t('contact-form-component.email.invalid-error'),
        phoneLabel: t('contact-form-component.phone.label'),
        phoneRequired: t('contact-form-component.phone.required-error'),
        subjectLabel: t('contact-form-component.subject.label'),
        subjectRequired: t('contact-form-component.subject.required-error'),
        messageLabel: t('contact-form-component.message.label'),
        messageRequired: t('contact-form-component.message.required-error'),
        recaptchaRequired: t('contact-form-component.recaptcha.required-error'),
        submitButton: t('contact-form-component.submit-button'),
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string[] } = {};

        if (!name.trim()) {
            newErrors.name = [translations.nameRequired];
        }

        if (!email.trim()) {
            newErrors.email = [translations.emailRequired];
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = [translations.emailInvalid];
        }

        if (!phone.trim()) {
            newErrors.phone = [translations.phoneRequired];
        }

        if (!subject.trim()) {
            newErrors.subject = [translations.subjectRequired];
        }

        if (!message.trim()) {
            newErrors.message = [translations.messageRequired];
        }

        if (!recaptchaToken) {
            newErrors.recaptcha = [translations.recaptchaRequired];
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSubmit({ name, email, phone, subject, message });
        }
    };

    const handleOnClickSubmit = () => {
        if (validateForm()) {
            formRef?.current?.requestSubmit();
        }
    };

    return (
        <div className={styles.container}>
            <Typography classes="mb-4" fontSize="24" fontWeight="900">
                {translations.title}
            </Typography>
            <Form formRef={formRef} classes="d-flex flex-column" onEnterPress={handleOnClickSubmit} onSubmit={handleSubmit}>
                <>
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={`${translations.nameLabel}*`}
                        value={name}
                        onChange={(value) => setName(value)}
                        state={errors.name ? InputStates.error : undefined}
                        errorMessages={errors.name}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    <Input
                        type={InputTypes.email}
                        variant={InputVariants.labeledInput}
                        label={`${translations.emailLabel}*`}
                        value={email}
                        onChange={(value) => setEmail(value)}
                        state={errors.email ? InputStates.error : undefined}
                        errorMessages={errors.email}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    <Input
                        type={InputTypes.tel}
                        variant={InputVariants.labeledInput}
                        label={`${translations.phoneLabel}*`}
                        value={phone}
                        onChange={(value) => setPhone(value)}
                        state={errors.phone ? InputStates.error : undefined}
                        errorMessages={errors.phone}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={`${translations.subjectLabel}*`}
                        value={subject}
                        onChange={(value) => setSubject(value)}
                        state={errors.subject ? InputStates.error : undefined}
                        errorMessages={errors.subject}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    <Input
                        type={InputTypes.textarea}
                        variant={InputVariants.labeledInput}
                        label={`${translations.messageLabel}*`}
                        value={message}
                        onChange={(value) => setMessage(value)}
                        state={errors.message ? InputStates.error : undefined}
                        errorMessages={errors.message}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                        onChange={(token) => setRecaptchaToken(token)}
                        onExpired={() => setRecaptchaToken(null)}
                    />
                    {errors.recaptcha && (
                        <div className="text-danger mt-2">
                            {errors.recaptcha.map((error, index) => (
                                <div key={index}>{error}</div>
                            ))}
                        </div>
                    )}

                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    <Button
                        label={translations.submitButton}
                        mode={ButtonMode.secondary}
                        classes="w-50"
                        onClick={handleOnClickSubmit}
                        disabled={loading}
                    />
                </>
            </Form>
        </div>
    );
};
